import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Image as ImageIcon, Layout, Ruler, Droplets, Hammer, Paintbrush, Save, AlertCircle, Upload, History, ChevronDown, Pencil, PenTool, Trash2, Copy } from 'lucide-react';
import RoofDesignTemplatePicker from '@/components/RoofDesignTemplatePicker';
import AerialCropModal from '@/components/AerialCropModal';
import SlotAnnotatorModal from '@/components/SlotAnnotatorModal';
import TraceTemplateModal from '@/components/TraceTemplateModal';
import { toast } from 'sonner';

/**
 * Four-slot per-appraisal Roof Diagram TEMPLATE picker. Diagrams are
 * pre-defined drawing templates from /api/roof-templates — NOT uploaded
 * photos. Each slot stores a templateId on the appraisal record:
 *
 *   1. Roof Plan        (base diagram — no measurements)
 *   2. Roofing          (plan for roofer measurements)
 *   3. Guttering        (plan for gutter measurements)
 *   4. Concrete Restore (plan for concrete-restore measurements)
 *   5. Painter          (plan for painter measurements)
 *
 * Behaviour:
 *   • Local "draft" state — changes don't hit the server until Save.
 *   • Save button is disabled while form == saved value. On click, a
 *     confirmation dialog summarises what's about to change.
 *   • Picking a template for "Roof Plan" auto-fills any of the other
 *     four slots that are currently EMPTY. (Existing picks aren't
 *     overwritten — the user must clear/change those manually.)
 *   • Clicking the slot's thumbnail/icon opens the full-screen
 *     SlotAnnotatorModal pre-loaded with THAT slot's template and the
 *     pins tagged for that slot.
 */
const SLOTS = [
  { key: 'roof_plan_template_id',                     label: 'Master Roof Plan', hint: 'Per-appraisal — measurements + master pins. Drives the 4 trade slots below.', icon: Layout,     colour: '#D35400' },
  { key: 'roofer_measurements_template_id',           label: 'Roofing',          hint: 'Roofer measurements',                icon: Ruler,      colour: '#1E40AF' },
  { key: 'guttering_measurements_template_id',        label: 'Guttering',        hint: 'Guttering measurements',             icon: Droplets,   colour: '#15803D' },
  { key: 'concrete_restore_measurements_template_id', label: 'Concrete Restore', hint: 'Concrete-restore measurements',      icon: Hammer,     colour: '#92400E' },
  { key: 'painter_measurements_template_id',          label: 'Painter',          hint: 'Painter measurements',               icon: Paintbrush, colour: '#7C3AED' },
];

export default function RoofDiagramSlotsCard({ api, appraisalId, appraisal, onChanged }) {
  // Saved state (what's on the server) and draft state (what the user is editing).
  const [saved, setSaved] = useState({});
  const [draft, setDraft] = useState({});
  const [snapCache, setSnapCache] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Iter182++ — trade slots collapsed by default so the Diagrams tab
  // opens focused on the Master Roof Plan.
  const [showTradeSlots, setShowTradeSlots] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Pending file → opens the crop modal. User may crop, skip, or cancel.
  const [pendingFile, setPendingFile] = useState(null);
  // Naming step — set after the crop modal confirms with the final file.
  // We hold the file + suggested default name + open the inline naming
  // modal. The window.prompt fallback was unreliable on some browsers
  // (it silently dismissed itself) so this is now state-driven.
  const [namingFile, setNamingFile]      = useState(null);
  const [namingDraft, setNamingDraft]    = useState('');
  // "Apply to all 5 slots" checkbox in the naming modal. Defaults to TRUE
  // because the user's intent in uploading a Google-Earth screenshot of
  // the property is almost always "this is the new base diagram for ALL
  // trades on this property" — not "stash this in the library only".
  const [applyToAllSlots, setApplyToAllSlots] = useState(true);
  // Per-slot annotator — set to the slot's key when the user clicks the
  // slot icon/thumbnail. Opens the full-screen SlotAnnotatorModal.
  const [editingSlotKey, setEditingSlotKey] = useState(null);
  // Trace tool — when set, opens the TraceTemplateModal seeded with this
  // aerial as the underlay so the operator can draw a clean line-art
  // template by tracing the roof outline.
  const [tracingAerial, setTracingAerial] = useState(null);
  // Property-specific aerials picker — populated on demand when the user
  // clicks "Use saved aerial".
  const [savedAerials, setSavedAerials] = useState([]);
  const [savedAerialsLoading, setSavedAerialsLoading] = useState(false);
  const [savedAerialsOpen, setSavedAerialsOpen] = useState(false);
  const savedMenuRef = useRef(null);
  const fileRef = useRef(null);

  // Iter182++ — cached property snapshot. Used as a render-fallback
  // for the Master Roof Plan slot when the appraisal's saved slot is
  // empty but the linked property has an accepted main template.
  // Fetched on mount + whenever propertyId changes; cheap (one GET).
  const propertyId = appraisal?.property_id || '';
  const [propertySnap, setPropertySnap] = useState(null);
  useEffect(() => {
    if (!propertyId) { setPropertySnap(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/properties/${propertyId}`);
        if (!cancelled) setPropertySnap(data);
      } catch { if (!cancelled) setPropertySnap(null); }
    })();
    return () => { cancelled = true; };
  }, [api, propertyId]);

  // Fetch property-specific aerials whenever the menu is opened. Aerials
  // are keyed to property_id so the user always sees only the ones they
  // uploaded for THIS address.
  const fetchSavedAerials = useCallback(async () => {
    if (!propertyId) { setSavedAerials([]); return; }
    setSavedAerialsLoading(true);
    try {
      const { data } = await api.get('/roof-templates', {
        params: { property_id: propertyId, aerial_only: true, limit: 20 },
      });
      setSavedAerials(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load saved aerials');
      setSavedAerials([]);
    } finally {
      setSavedAerialsLoading(false);
    }
  }, [api, propertyId]);

  // Pre-fetch the count on mount so the button label can show "(N)" badge.
  useEffect(() => {
    if (propertyId) fetchSavedAerials();
  }, [propertyId, fetchSavedAerials]);

  // Close the dropdown on outside-click.
  useEffect(() => {
    if (!savedAerialsOpen) return;
    const onDoc = (e) => {
      if (savedMenuRef.current && !savedMenuRef.current.contains(e.target)) {
        setSavedAerialsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [savedAerialsOpen]);

  // One-click apply: fill all 5 Roof Diagram slots with the chosen aerial
  // template id. Same DRAFT-only semantics as the existing template
  // picker — the user must hit Save to persist.
  const applySavedAerial = (template) => {
    if (!template?._id) return;
    setDraft(prev => {
      const next = { ...prev };
      for (const s of SLOTS) next[s.key] = template._id;
      return next;
    });
    setSnapCache(c => ({
      ...c,
      [template._id]: {
        _id: template._id,
        name: template.name,
        thumbnail_url: `/api/roof-templates/${template._id}/image`,
      },
    }));
    setSavedAerialsOpen(false);
    toast.success(`Loaded "${template.name}" into all 5 slots — click Save to attach.`);
  };

  // Step 1: file picker callback — just stash the file + open crop modal.
  const onFilePicked = (file) => {
    if (!file) return;
    if (!propertyId) {
      toast.error("No property linked to this appraisal — can't tag the diagram against a property.");
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setPendingFile(file);
  };

  // Step 2: crop modal confirms with the (possibly cropped) File. We now
  // surface the naming modal — the operator can rename the diagram before
  // it gets uploaded. Replaces the old window.prompt() which was being
  // silently blocked in some browsers.
  const onCropConfirmed = (finalFile) => {
    setPendingFile(null);
    const prop = appraisal?.property || {};
    const addr = [prop.property_address_num, prop.property_address_street, prop.property_address_suburb]
      .filter(Boolean).join(' ').trim();
    const date = new Date().toISOString().slice(0, 10);
    const defaultName = `Aerial — ${addr || 'Property ' + propertyId.slice(-6)} — ${date}`;
    setNamingDraft(defaultName);
    setNamingFile(finalFile);
  };

  const onNamingCancel = () => {
    setNamingFile(null);
    setNamingDraft('');
    setApplyToAllSlots(true);  // reset for next time
    if (fileRef.current) fileRef.current.value = '';
  };

  // Step 3: user confirmed the name → actually upload.
  const performUpload = async () => {
    if (!namingFile) return;
    const finalFile = namingFile;
    const name = (namingDraft || '').trim() || finalFile.name || 'Aerial';
    setNamingFile(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', finalFile);
      form.append('name', name);
      form.append('property_id', propertyId);
      form.append('aerial', 'true');
      form.append('category', 'Property Aerial');
      const { data } = await api.post('/roof-templates/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDraft(prev => {
        const next = { ...prev };
        if (applyToAllSlots) {
          // Treat this aerial as the new master for the property. Fill ALL
          // 5 slots so the trade editors share the same canvas — otherwise
          // "Pull from Roof Plan" copies pins onto the wrong diagram.
          for (const s of SLOTS) next[s.key] = data._id;
        } else {
          // Library-only mode: only fill empty slots.
          for (const s of SLOTS) if (!next[s.key]) next[s.key] = data._id;
          if (!next.roof_plan_template_id) next.roof_plan_template_id = data._id;
        }
        return next;
      });
      // Also mirror to the legacy header picker so the operator sees the
      // upload reflected on the "Property Base Diagram" picker. Done as a
      // direct PATCH (not via DRAFT) because the upload itself is already
      // an immediate action.
      if (applyToAllSlots) {
        try {
          await api.patch(`/appraisals/${appraisalId}/roof-template`, { roof_design_template_id: data._id });
        } catch { /* non-fatal */ }
      }
      setSnapCache(c => ({ ...c, [data._id]: { _id: data._id, name: data.name, thumbnail_url: `/api/roof-templates/${data._id}/image` } }));
      toast.success(
        applyToAllSlots
          ? `Uploaded "${data.name}" → set as base diagram for all 5 slots. Click Save to attach.`
          : `Uploaded "${data.name}" — only empty slots were filled. Click Save to attach.`
      );
      // Re-fetch saved aerials so the new upload shows up in the picker next time.
      fetchSavedAerials();
    } catch (e) {
      // Verbose diagnostic — surface exact backend status + reason so the
      // user can tell us what blew up. Also logs to the JS console.
      const status   = e?.response?.status;
      const detail   = e?.response?.data?.detail || e?.response?.data?.message;
      const netErr   = e?.message;
      const reason = detail
        ? `${detail}${status ? ` (HTTP ${status})` : ''}`
        : status
          ? `Upload failed — HTTP ${status}`
          : netErr
            ? `Upload failed — ${netErr}`
            : 'Upload failed — unknown error';
      console.error('[Upload Aerial] failed', { status, detail, netErr, error: e });
      toast.error(reason, { duration: 8000 });
    } finally {
      setUploading(false);
      setNamingDraft('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onCropCancelled = () => {
    setPendingFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    const next = {};
    for (const s of SLOTS) next[s.key] = (appraisal || {})[s.key] || '';
    setSaved(next);
    setDraft(next);
  }, [appraisal]);

  // Iter181 — when the appraisal has NO saved template slots yet but
  // the linked property has an accepted Main Roof Template, pre-fill
  // all 5 slots with that template as the DRAFT so the operator just
  // has to hit Save. This closes the property→appraisal handoff: the
  // template they accepted on the property page shows up here without
  // a manual pick.
  useEffect(() => {
    if (!propertyId) return;
    const anySaved = SLOTS.some(s => (appraisal || {})[s.key]);
    if (anySaved) return;
    (async () => {
      try {
        const { data: prop } = await api.get(`/properties/${propertyId}`);
        const tplId = prop?.roof_template_id;
        if (!tplId) return;
        const { data: tpl } = await api.get(`/roof-templates/${tplId}`);
        if (!tpl?._id) return;
        // Seed the cache so the slot thumbnails render instantly.
        setSnapCache(c => ({ ...c, [tpl._id]: {
          _id: tpl._id,
          name: tpl.name,
          thumbnail_url: tpl.thumbnail_url || `/api/roof-templates/${tpl._id}/image`,
        }}));
        setDraft(d => {
          const next = { ...d };
          for (const s of SLOTS) if (!next[s.key]) next[s.key] = tpl._id;
          return next;
        });
        toast.message(`Pre-loaded "${tpl.name}" from this property's Main Roof Template — click Save to attach.`);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, appraisal?._id]);

  // Resolve thumbnails for any template id we haven't seen yet.
  const resolveSnap = useCallback(async (id) => {
    if (!id || snapCache[id]) return;
    try {
      const { data } = await api.get(`/roof-templates/${id}`);
      setSnapCache(c => ({ ...c, [id]: { _id: data._id, name: data.name, thumbnail_url: `/api/roof-templates/${data._id}/image` } }));
    } catch { /* ignore */ }
  }, [api, snapCache]);

  useEffect(() => {
    for (const s of SLOTS) {
      const id = draft[s.key];
      if (id && !snapCache[id]) resolveSnap(id);
    }
  }, [draft, resolveSnap, snapCache]);

  const dirty = useMemo(() => {
    return SLOTS.some(s => (draft[s.key] || '') !== (saved[s.key] || ''));
  }, [draft, saved]);

  const changedSummary = useMemo(() => {
    return SLOTS.flatMap(s => {
      const a = saved[s.key] || '';
      const b = draft[s.key] || '';
      if (a === b) return [];
      const from = a ? (snapCache[a]?.name || a.slice(-6)) : '— empty —';
      const to   = b ? (snapCache[b]?.name || b.slice(-6)) : '— cleared —';
      return [{ label: s.label, from, to }];
    });
  }, [draft, saved, snapCache]);

  const onSlotChange = (slotKey, templateId) => {
    setDraft(d => {
      const next = { ...d, [slotKey]: templateId || '' };
      // Cascade when the Master Roof Plan slot is changed: ALWAYS overwrite
      // every trade slot to match. This matches the operator's expectation
      // that "the Master IS the diagram for this appraisal — the trade
      // slots inherit it". If the operator wants a different template on a
      // specific trade slot, they pick it AFTER setting the Master (the
      // per-slot picker stays available). The amber mismatch banner
      // surfaces any drift on subsequent edits.
      if (slotKey === 'roof_plan_template_id' && templateId) {
        for (const s of SLOTS) {
          if (s.key === slotKey) continue;
          next[s.key] = templateId;
        }
      }
      return next;
    });
  };

  /**
   * Force a trade slot to mirror the current Master Roof Plan template.
   * Distinct from `onSlotChange` (which is the no-overwrite cascade above)
   * — this OVERWRITES whatever template the trade slot is on. Used by the
   * per-card "Sync from Master" button.
   */
  const syncSlotFromMaster = (slotKey) => {
    const masterId = draft.roof_plan_template_id || '';
    if (!masterId) {
      toast.error('Set the Master Roof Plan first.');
      return;
    }
    if (slotKey === 'roof_plan_template_id') return;
    setDraft(d => ({ ...d, [slotKey]: masterId }));
    toast.success(`${SLOTS.find(s => s.key === slotKey)?.label || 'Slot'} now mirrors the Master Roof Plan. Hit Save to commit.`);
  };

  /** Force ALL four trade slots to mirror the Master. */
  const syncAllFromMaster = () => {
    const masterId = draft.roof_plan_template_id || '';
    if (!masterId) {
      toast.error('Set the Master Roof Plan first.');
      return;
    }
    setDraft(d => {
      const next = { ...d };
      for (const s of SLOTS) {
        if (s.key !== 'roof_plan_template_id') next[s.key] = masterId;
      }
      return next;
    });
    toast.success('All trade slots now mirror the Master Roof Plan. Hit Save to commit.');
  };

  const performSave = async () => {
    setSaving(true);
    try {
      // Only PATCH the fields that actually changed.
      const partial = {};
      for (const s of SLOTS) {
        if ((draft[s.key] || '') !== (saved[s.key] || '')) {
          partial[s.key] = draft[s.key] || '';
        }
      }
      // Mirror Roof Plan to the legacy `roof_design_template_id` field too
      // so the "Property Base Diagram" picker on the page header stays in
      // lockstep with the slots (single source of truth for the operator).
      if ('roof_plan_template_id' in partial) {
        partial.roof_design_template_id = partial.roof_plan_template_id;
      }
      await api.patch(`/appraisals/${appraisalId}/roof-template`, partial);
      setSaved(draft);
      setConfirmOpen(false);
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const resetDraft = () => setDraft(saved);

  return (
    <div data-testid="roof-diagram-slots-card" className="bg-white border border-[#E2E2DF] rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 uppercase tracking-[0.14em]">
          <ImageIcon size={14} className="text-[#D35400]" /> Roof Diagrams
        </h3>
        <div className="flex items-center gap-2">
          {dirty && (
            <span data-testid="roof-slots-dirty-indicator" className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
              Unsaved · {changedSummary.length} change{changedSummary.length === 1 ? '' : 's'}
            </span>
          )}
          {dirty && (
            <button
              data-testid="roof-slots-reset-btn"
              type="button"
              onClick={resetDraft}
              className="text-xs px-2.5 py-1 rounded-md border border-[#E2E2DF] text-slate-600 hover:bg-slate-50"
            >Reset</button>
          )}
          <button
            data-testid="roof-slots-upload-btn"
            type="button"
            disabled={uploading || !propertyId}
            onClick={() => fileRef.current?.click()}
            title={propertyId ? 'Upload a Google Earth screenshot or custom diagram for THIS property. Saved as a new template linked to the property.' : 'Link a property to this appraisal first.'}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white transition-all disabled:opacity-40"
          >
            <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload Aerial'}
          </button>
          {/* Quick-fill: pick a previously-uploaded aerial saved against this
              property. Inverts on the most common path (repeat appraisals on
              the same address). */}
          <div className="relative" ref={savedMenuRef}>
            <button
              data-testid="roof-slots-use-saved-btn"
              type="button"
              disabled={!propertyId || savedAerials.length === 0}
              onClick={() => { fetchSavedAerials(); setSavedAerialsOpen(o => !o); }}
              title={
                !propertyId
                  ? 'Link a property to this appraisal first.'
                  : savedAerials.length === 0
                    ? 'Upload an aerial first — saved aerials for this property will appear here.'
                    : `Pick from ${savedAerials.length} aerial${savedAerials.length === 1 ? '' : 's'} previously uploaded for this property.`
              }
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#D35400] text-[#D35400] hover:bg-[#D35400] hover:text-white transition-all disabled:opacity-40 disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent disabled:hover:text-slate-400"
            >
              <History size={12} /> Use saved aerial
              {savedAerials.length > 0 && (
                <span data-testid="roof-slots-saved-count" className="text-[10px] font-mono-num bg-[#D35400] text-white px-1 rounded">{savedAerials.length}</span>
              )}
              <ChevronDown size={12} className={`transition-transform ${savedAerialsOpen ? 'rotate-180' : ''}`} />
            </button>
            {savedAerialsOpen && (
              <div
                data-testid="roof-slots-saved-menu"
                className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-md shadow-lg w-72 max-h-[60vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-slate-50 px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">
                  Aerials uploaded for this property
                </div>
                {savedAerialsLoading && (
                  <div className="p-3 text-xs text-slate-500">Loading…</div>
                )}
                {!savedAerialsLoading && savedAerials.length === 0 && (
                  <div className="p-3 text-xs text-slate-500">
                    No aerials saved yet. Click <strong>Upload Aerial</strong> to add one.
                  </div>
                )}
                {!savedAerialsLoading && savedAerials.map((t, idx) => (
                  <div
                    key={t._id}
                    data-testid={`roof-slots-saved-item-${idx}`}
                    className="w-full flex items-center gap-2 px-2 py-2 border-b border-slate-100 last:border-b-0 hover:bg-[#FFF7ED]"
                  >
                    <button
                      type="button"
                      onClick={() => applySavedAerial(t)}
                      title="Load this aerial into all slots"
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      <img
                        src={`/api/roof-templates/${t._id}/image`}
                        alt={t.name}
                        className="w-12 h-12 object-cover rounded border border-slate-200 bg-slate-50 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate">{t.name}</p>
                        <p className="text-[10px] text-slate-400">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                          {idx === 0 && <span className="ml-1.5 px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider text-[9px]">Latest</span>}
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid={`roof-slots-saved-trace-${idx}`}
                      onClick={(e) => { e.stopPropagation(); setSavedAerialsOpen(false); setTracingAerial(t); }}
                      title="Trace this aerial to create a new clean line-art template"
                      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white transition-colors flex-shrink-0"
                    ><PenTool size={10} /> Trace</button>
                    <button
                      type="button"
                      data-testid={`roof-slots-saved-delete-${idx}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Permanently delete the saved aerial / traced photo "${t.name}"?\n\nIt will be removed from this property AND the global library.`)) return;
                        try {
                          await api.delete(`/roof-templates/${t._id}`);
                          toast.success(`Deleted "${t.name}".`);
                          fetchSavedAerials();
                          onChanged?.();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Delete failed.');
                        }
                      }}
                      title="Delete this saved aerial / traced photo. Cannot be undone."
                      className="flex items-center justify-center w-6 h-6 rounded border border-rose-300 text-rose-500 hover:bg-rose-500 hover:text-white transition-colors flex-shrink-0"
                    ><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            data-testid="roof-slots-upload-input"
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            onChange={e => onFilePicked(e.target.files?.[0])}
          />
          <button
            data-testid="roof-slots-save-btn"
            type="button"
            disabled={!dirty || saving}
            onClick={() => setConfirmOpen(true)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-bold transition-colors ${dirty
              ? 'bg-[#D35400] text-white hover:bg-[#B84500]'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 -mt-1">
        Pick from the roof-template library, or click <strong>Upload Aerial</strong> to add a Google Earth / drone / sketched screenshot for this property. Setting <strong>Master Roof Plan</strong> auto-fills the other four empty slots — hit <strong>Mirror Master to all trades</strong> below to overwrite the existing trade templates.
      </p>
      {/* One-click cascade — overwrites all four trade slots with the
          Master Roof Plan template. Only enabled when Master is set AND
          at least one trade slot is on a different template. */}
      {draft.roof_plan_template_id && SLOTS.slice(1).some(s => (draft[s.key] || '') !== (draft.roof_plan_template_id || '')) && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-center justify-between gap-2 flex-wrap" data-testid="mirror-master-banner">
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-amber-900">Some trade slots don't match the Master Roof Plan</p>
            <p className="text-[10px] text-amber-700 mt-0.5">
              Mirror the Master template to Roofing / Guttering / Concrete Restore / Painter in one click. You can still pin-edit each one independently afterwards.
            </p>
          </div>
          <button
            type="button"
            data-testid="mirror-master-to-all-btn"
            onClick={syncAllFromMaster}
            className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors"
          >
            <Copy size={12} /> Mirror Master to all trades
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SLOTS.filter(s => showTradeSlots || s.key === 'roof_plan_template_id').map(slot => {
          const Icon = slot.icon;
          const tid = draft[slot.key] || '';
          const snap = tid ? snapCache[tid] : null;
          const masterId = draft.roof_plan_template_id || '';
          const isMaster = slot.key === 'roof_plan_template_id';
          const mismatches = !isMaster && masterId && tid && tid !== masterId;
          return (
            <SlotRow
              key={slot.key}
              testId={`roof-slot-${slot.key}`}
              label={slot.label}
              hint={slot.hint}
              Icon={Icon}
              colour={slot.colour}
              templateId={tid}
              snap={snap}
              onChange={(newId) => onSlotChange(slot.key, newId)}
              onOpenEditor={() => setEditingSlotKey(slot.key)}
              mismatchesMaster={mismatches}
              showSyncButton={!isMaster && Boolean(masterId)}
              onSyncFromMaster={() => syncSlotFromMaster(slot.key)}
            />
          );
        })}
      </div>
      {/* Iter182++ — Operators consistently say they only update the Master
          Roof Plan on first visit; the 4 trade slots come later. Default
          to collapsed so the page opens focused, with one click to expand. */}
      <button
        data-testid="roof-diagrams-toggle-trade-slots"
        type="button"
        onClick={() => setShowTradeSlots(s => !s)}
        className="self-start mt-2 text-[11px] font-semibold text-[#D35400] hover:text-[#A04000] flex items-center gap-1 px-2 py-1 rounded border border-dashed border-[#D35400]/40 hover:bg-orange-50/60"
        title={showTradeSlots ? 'Hide the per-trade slots' : 'Show Roofer / Guttering / Concrete / Painter slots'}
      >
        {showTradeSlots ? '− Hide' : '+ Show'} the 4 trade slots
        {!showTradeSlots && <span className="font-normal text-slate-500 ml-1">(Roofing · Guttering · Concrete Restore · Painter)</span>}
      </button>

      {confirmOpen && (
        <div data-testid="roof-slots-confirm-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setConfirmOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle size={16} />
              <h2 className="text-sm font-bold uppercase tracking-[0.14em]">Save roof diagram changes?</h2>
            </div>
            <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-3 text-xs space-y-1.5 max-h-60 overflow-y-auto">
              {changedSummary.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="font-semibold text-slate-700 min-w-[110px]">{c.label}:</span>
                  <span className="text-slate-500">{c.from}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-slate-800 font-semibold">{c.to}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={saving}
                className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >Cancel</button>
              <button
                data-testid="roof-slots-confirm-save-btn"
                onClick={performSave}
                disabled={saving}
                className="px-3 py-1.5 bg-[#D35400] text-white rounded-md text-sm font-bold hover:bg-[#B84500] disabled:opacity-60"
              >{saving ? 'Saving…' : 'Confirm save'}</button>
            </div>
          </div>
        </div>
      )}
      {pendingFile && (
        <AerialCropModal file={pendingFile} onConfirm={onCropConfirmed} onCancel={onCropCancelled} />
      )}

      {/* Naming modal — replaces the unreliable window.prompt() */}
      {namingFile && (
        <div data-testid="aerial-name-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onNamingCancel}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-800">Name your new diagram</h2>
              <p className="text-[11px] text-slate-500 mt-1">
                This name appears in the template picker for future appraisals at this property.
                <br/><span className="text-slate-400">File: {namingFile.name} · {(namingFile.size / 1024).toFixed(0)} KB</span>
              </p>
            </div>
            <input
              data-testid="aerial-name-input"
              autoFocus
              value={namingDraft}
              onChange={e => setNamingDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') performUpload();
                if (e.key === 'Escape') onNamingCancel();
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#D35400]"
              placeholder="e.g. Aerial — 123 Smith St — 2026-02"
            />
            <label
              data-testid="aerial-name-apply-all-label"
              className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer select-none p-2 -mx-1 rounded hover:bg-slate-50"
            >
              <input
                data-testid="aerial-name-apply-all"
                type="checkbox"
                checked={applyToAllSlots}
                onChange={e => setApplyToAllSlots(e.target.checked)}
                className="mt-0.5 accent-[#D35400]"
              />
              <span>
                <strong className="text-slate-700">Set as base diagram for all 5 slots</strong>
                <span className="block text-slate-400 mt-0.5">
                  Recommended for a property aerial — keeps Master Roof Plan / Roofing / Guttering / Concrete Restore / Painter in sync so &ldquo;Pull from Master Roof Plan&rdquo; copies pins onto the same canvas. Untick to add to the library only.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                data-testid="aerial-name-cancel"
                onClick={onNamingCancel}
                className="px-3 py-1.5 border border-slate-200 rounded-md text-sm text-slate-600 hover:bg-slate-50"
              >Cancel</button>
              <button
                data-testid="aerial-name-confirm"
                onClick={performUpload}
                disabled={!namingDraft.trim()}
                className="px-3 py-1.5 bg-[#D35400] text-white rounded-md text-sm font-bold hover:bg-[#B84500] disabled:opacity-40"
              >Upload diagram</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-slot annotator — opens when user clicks a slot's
          thumbnail/icon. Loads THAT slot's template + pins. */}
      {editingSlotKey && (() => {
        const slotDef = SLOTS.find(s => s.key === editingSlotKey);
        // Iter182++ — final-fallback template id. When the operator
        // clicks "Edit Master Plan" before clicking Save on the slot
        // card, the appraisal's saved slot is still empty. Fall back
        // to the linked property's `roof_template_id` so the editor
        // never opens blank when the property HAS an accepted main
        // template. Doesn't write anything to the DB — purely a render
        // fallback. The proper save still has to flow through the
        // Save button on the slots card.
        const fallbackTplId = propertySnap?.roof_template_id || '';
        const effectiveSlotTpl = draft[editingSlotKey] || saved[editingSlotKey]
          || (editingSlotKey === 'roof_plan_template_id' ? fallbackTplId : '');
        const effectiveMasterTpl = draft.roof_plan_template_id || saved.roof_plan_template_id || fallbackTplId;
        return (
          <SlotAnnotatorModal
            open
            slotKey={editingSlotKey}
            slotLabel={slotDef?.label}
            slotColour={slotDef?.colour}
            appraisalId={appraisalId}
            templateId={effectiveSlotTpl}
            roofPlanTemplateId={effectiveMasterTpl}
            pins={appraisal?.roof_diagram_annotations || []}
            roofProfile={(appraisal?.roof_details || [])[0]?.roof_profile || ''}
            roofType={(appraisal?.roof_details || [])[0]?.roof_type || ''}
            onPinsChange={(merged) => {
              // When the operator finishes annotating the Master Roof Plan,
              // surface a one-click prompt to cascade the Master template to
              // any trade slots still on a stale value. We don't auto-apply
              // because the operator may have deliberately picked different
              // per-trade templates — but we DO nudge.
              if (editingSlotKey === 'roof_plan_template_id') {
                const masterId = draft.roof_plan_template_id || saved.roof_plan_template_id || '';
                const stale = SLOTS.slice(1).filter(s => masterId && (draft[s.key] || '') !== masterId);
                if (masterId && stale.length > 0) {
                  toast(`Master Roof Plan was updated — ${stale.length} trade slot${stale.length === 1 ? '' : 's'} still on a different template.`, {
                    duration: 8000,
                    action: {
                      label: 'Mirror Master to all trades',
                      onClick: () => syncAllFromMaster(),
                    },
                  });
                }
              }
              onChanged?.();
            }}
            onTemplateAligned={(key, newTplId) => {
              // The Pull action re-pointed this slot's canvas at the Roof
              // Plan template. Reflect that in BOTH our draft state (so the
              // slot's thumbnail/picker updates) AND the saved baseline (so
              // the dirty indicator doesn't light up — the change is already
              // persisted server-side by the Pull endpoint).
              setDraft(prev => ({ ...prev, [key]: newTplId }));
              onChanged?.();
            }}
            onClose={() => setEditingSlotKey(null)}
            api={api}
          />
        );
      })()}

      {/* Trace tool — turns an aerial screenshot into a clean line-art
          template by letting the operator draw on top of it. */}
      {tracingAerial && (
        <TraceTemplateModal
          open
          aerial={tracingAerial}
          propertyId={propertyId}
          appraisalId={appraisalId}
          api={api}
          defaultName={tracingAerial?.name ? `Traced — ${tracingAerial.name}` : 'Traced template'}
          onSaved={() => {
            toast.success('New template saved — also available in the picker library.');
            fetchSavedAerials();
            onChanged?.();
          }}
          onClose={() => setTracingAerial(null)}
        />
      )}
    </div>
  );
}

function SlotRow({ label, hint, Icon, colour = '#D35400', templateId, snap, onChange, onOpenEditor, testId, mismatchesMaster, showSyncButton, onSyncFromMaster }) {
  const hasTpl = !!templateId;
  return (
    <div data-testid={testId} className={`bg-slate-50 border rounded-lg p-3 flex items-center gap-3 transition-colors ${mismatchesMaster ? 'border-amber-300 ring-1 ring-amber-200' : 'border-[#E2E2DF]'}`}>
      {/* Clickable icon — opens the per-slot annotator. Doubles as a
          live thumbnail when a template is selected so the operator can
          see at a glance what's loaded. */}
      <button
        type="button"
        data-testid={`${testId}-open-editor`}
        onClick={onOpenEditor}
        disabled={!hasTpl}
        title={hasTpl ? `Open ${label} diagram to add/edit pins` : 'Pick a template first, then click to start drawing.'}
        className={`w-12 h-12 rounded-md overflow-hidden flex-shrink-0 border transition-all ${
          hasTpl
            ? 'border-slate-200 hover:ring-2 hover:ring-[#D35400] hover:border-[#D35400] cursor-pointer bg-white'
            : 'border-dashed border-slate-300 bg-slate-100 cursor-not-allowed'
        }`}
        style={hasTpl ? {} : { color: colour }}
      >
        {hasTpl && snap?.thumbnail_url ? (
          <img src={snap.thumbnail_url} alt={snap.name || label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `${colour}15` }}>
            <Icon size={18} style={{ color: colour }} />
          </div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500 flex items-center gap-1.5">
          {label}
          {mismatchesMaster && (
            <span data-testid={`${testId}-mismatch-tag`} className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 bg-amber-100 text-amber-800 ring-1 ring-amber-200 rounded">
              <AlertCircle size={8} /> off-master
            </span>
          )}
        </p>
        {!templateId && <p className="text-[11px] text-slate-400 truncate mt-0.5">{hint}</p>}
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {hasTpl && (
            <button
              type="button"
              data-testid={`${testId}-edit-link`}
              onClick={onOpenEditor}
              className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
              style={{ color: colour }}
            >
              <Pencil size={10} /> Edit / add pins
            </button>
          )}
          {/* Per-row "Sync from Master" — overwrites this trade slot's
              template with the Master Roof Plan's. Hidden on the Master
              row itself, hidden when there's no Master yet. */}
          {showSyncButton && (
            <button
              type="button"
              data-testid={`${testId}-sync-from-master`}
              onClick={onSyncFromMaster}
              title="Replace this slot's template with the Master Roof Plan's. Pins for this trade can then be pulled in via the slot's annotator."
              className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-200 px-1.5 py-0.5 rounded transition-colors"
            >
              <Copy size={10} /> Sync from Master
            </button>
          )}
        </div>
      </div>
      <div className="flex-shrink-0">
        <RoofDesignTemplatePicker
          value={templateId}
          selectedSnap={snap}
          onChange={(id) => onChange(id || '')}
        />
      </div>
    </div>
  );
}
